import { ClientContentItemEntity, IContentItemListFilters } from '@edgebox/sync-core-rest-client';
import { ProgressBar } from './Components/ProgressBar';
import {
  ContentItemStatus,
  ContentPriority,
  ContentRepresentation,
  IRuntimeTermAssignment,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
  TaskDomain,
  TaskGroupType,
} from '@edgebox/sync-core-data-definitions';
import { InternalId } from '@edgebox/data-definition-kit';
import { faArrowsRotate, faArrowUpRightFromSquare, faFileLines, faMagnifyingGlass, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, ButtonLink, Form } from '@edgebox/react-components';
import { Link, useNavigate } from 'react-router';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { sendToParent } from '../../../frame-messages';
import { AddFilterDropdown, IInventoryFilterValue } from './Onboarding/ContentInventory/Components/AddFilterDropdown';
import { ISystemColumn, ColumnPicker } from './Onboarding/ContentInventory/Components/ColumnPicker';
import { HealthCheckCriticalityBadge } from './Onboarding/ContentInventory/Components/HealthCheckCriticalityBadge';
import { HealthCheckNameReference } from './Onboarding/ContentInventory/Components/HealthCheckNameReference';
import { HealthCheckScoreBadge } from './Onboarding/ContentInventory/Components/HealthCheckScoreBadge';
import { ContentItemStatusIcon } from './Onboarding/ContentInventory/Components/ContentItemStatusIcon';
import { TaxonomyTermNameBadge } from './Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { getCachedTaxonomies } from './Onboarding/ContentInventory/Components/useCachedTaxonomyData';
import { ContentItemActionDropdown, ContentItemBulkAction } from './Onboarding/ContentInventory/Components/ContentItemActionDropdown';
import { ManageTermsActionDropdown } from './Onboarding/ContentInventory/Components/ManageTermsActionDropdown';
import { GovernanceSortableHeader } from './Components/GovernanceSortableHeader';
import { useBrandPresenceOnboarding } from './BrandPresenceOnboardingProvider';
import {
  BRAND_PRESENCE_CONTENT_INVENTORY_QUERY_PARAMS,
  BRAND_PRESENCE_CRAWLING_SETTINGS_PATH,
  brandPresenceContentItemPath,
} from './brandPresencePaths';
import { toBrandPresenceSearch, useBrandPresenceScreenAddress, useReportBrandPresenceScreenQuery } from './brandPresenceScreenQuery';
import {
  BRAND_PRESENCE_LIST_PAGE_SIZES,
  BRAND_PRESENCE_LIST_SEARCH_DEBOUNCE_MS,
  pageSizeToToken,
  parsePageSizeToken,
} from './Components/brandPresenceLists';
import { BrandPresencePageHeader } from './Components/BrandPresencePageHeader';
import { BrandPresenceEmptyState } from './Components/BrandPresenceEmptyState';
import { BrandPresencePagination } from './Components/BrandPresencePagination';
import { IndeterminateProgress } from './Components/BrandPresenceSkeleton';
import { ContentInventorySkeleton, ContentInventorySkeletonRows } from './Components/ContentInventorySkeleton';
import { GovernanceAnalyticsKpiCard, KpiMeter } from './Components/GovernanceAnalyticsKpiCard';
import { GovernanceAnalyticsKpiCardRow } from './Components/GovernanceAnalyticsKpiCardRow';
import { HEALTH_METER_FILL_CLASSES, HealthScoreCell } from './Components/HealthScoreCell';
import { resolveHealthCheckScorePresentation } from './Onboarding/ContentInventory/Components/HealthCheckScorePresentation';
import {
  BP_CARD_CLASSES,
  BP_PRIMARY_BUTTON_CLASSES,
  BP_TABLE_HEAD_CELL_CLASSES,
  BP_TOOLBAR_BUTTON_CLASSES,
} from './Components/brandPresenceClasses';

type WorstCheckModel = {
  healthCheckKey: string;
  score: number;
};

type RowModel = {
  id: string;
  key: string;
  name: string;
  score?: number;
  contentStatus?: ContentItemStatus;
  worstChecks: WorstCheckModel[];
  taxonomyValuesByFacet: Partial<Record<TaxonomyFacet, string[]>>;
  priority: string;
  age: string;
  url?: string;
};

const DEFAULT_TAXONOMY_COLUMNS: TaxonomyFacet[] = [TaxonomyFacet.AuthorityTopic, TaxonomyFacet.Brand];
const QUERY_ORDER_PARAM = 'order';
const QUERY_COLUMNS_PARAM = 'columns';
const QUERY_STATUS_PARAM = BRAND_PRESENCE_CONTENT_INVENTORY_QUERY_PARAMS.status;
const QUERY_PRIORITY_PARAM = 'priority';
const QUERY_TERMS_PARAM = BRAND_PRESENCE_CONTENT_INVENTORY_QUERY_PARAMS.terms;
const QUERY_TYPE_PARAM = BRAND_PRESENCE_CONTENT_INVENTORY_QUERY_PARAMS.type;
const QUERY_SEARCH_PARAM = 'search';
const QUERY_PAGE_SIZE_PARAM = BRAND_PRESENCE_CONTENT_INVENTORY_QUERY_PARAMS.pageSize;
const FIXED_COLUMNS = ['name', 'age'];
const DEFAULT_VISIBLE_SYSTEM_COLUMNS: ISystemColumn[] = ['score', 'status', 'worstChecks'];

type InventoryOrderBy = 'default' | 'name' | 'priority' | 'lastCrawledAt' | 'healthScore';
type InventoryOrderDirection = 'asc' | 'desc';
type SortableInventoryOrderBy = Exclude<InventoryOrderBy, 'default'>;
type TaskGroupSummaryItem = { status?: string; count?: number };
type CrawlProgress = { progress: number; title: string; finished: boolean };

const ORDER_DEFAULT_DIRECTION: Record<SortableInventoryOrderBy, InventoryOrderDirection> = {
  name: 'asc',
  priority: 'desc',
  lastCrawledAt: 'desc',
  healthScore: 'desc',
};

function isTaxonomyTermKey(value: string): boolean {
  const normalized = value.trim();
  return normalized.includes('.') && !normalized.includes('::');
}

function toListOrder(orderBy: InventoryOrderBy, orderDirection: InventoryOrderDirection): IContentItemListFilters['order'] | undefined {
  if (orderBy && orderBy !== 'default') {
    return orderDirection === 'asc' ? `+${orderBy}` : `-${orderBy}`;
  }

  return undefined;
}

function buildContentItemListFilters(
  selectedFilters: IInventoryFilterValue[],
  search: string,
  orderBy: InventoryOrderBy,
  orderDirection: InventoryOrderDirection
): IContentItemListFilters {
  const status = new Set<ContentItemStatus>();
  const priority = new Set<ContentPriority>();
  const taxonomyTerms = new Set<string>();
  let type: ContentRepresentation | undefined;

  for (const filter of selectedFilters) {
    if (filter.targetType === 'type') {
      type = toContentType(filter.valueId);
      continue;
    }

    if (filter.targetType === 'status') {
      status.add(filter.valueId as ContentItemStatus);
      continue;
    }

    if (filter.targetType === 'priority') {
      priority.add(filter.valueId as ContentPriority);
      continue;
    }

    if (filter.targetType === 'taxonomy') {
      if (isTaxonomyTermKey(filter.valueId)) {
        taxonomyTerms.add(filter.valueId);
      }
    }
  }

  const query: IContentItemListFilters = {
    order: toListOrder(orderBy, orderDirection),
    type,
    status: status.size ? Array.from(status) : undefined,
    priority: priority.size ? Array.from(priority) : undefined,
    taxonomyTerms: taxonomyTerms.size ? Array.from(taxonomyTerms) : undefined,
    search: search.trim() || undefined,
  };

  return query;
}

/** The content type a query token names, or nothing where it names none the list knows. */
function toContentType(value: string): ContentRepresentation | undefined {
  return (Object.values(ContentRepresentation) as string[]).includes(value) ? (value as ContentRepresentation) : undefined;
}

function getDefaultPublishedFilter(): IInventoryFilterValue {
  return {
    targetId: 'status',
    targetLabel: 'Status',
    targetType: 'status',
    valueId: ContentItemStatus.Published,
    valueLabel: SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('ContentItemStatus', ContentItemStatus.Published),
  };
}

function serializeFiltersParam(filters: IInventoryFilterValue[]): string {
  return JSON.stringify(
    filters.map((filter) => ({
      targetId: filter.targetId,
      targetLabel: filter.targetLabel,
      targetType: filter.targetType,
      valueId: filter.valueId,
      valueLabel: filter.valueLabel,
    }))
  );
}

/** The filters a fresh inventory opens with; anything else means the reader narrowed the list. */
const DEFAULT_FILTERS_SERIALIZED = serializeFiltersParam([getDefaultPublishedFilter()]);

/** Shown in place of a value the page does not have. */
const EM_DASH = '—';

function coerceQueryStringValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    const firstString = value.find((entry) => typeof entry === 'string');
    return typeof firstString === 'string' ? firstString : undefined;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return undefined;
}

function parseCsvValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .flatMap((entry) => parseCsvValue(entry))
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  const serialized = coerceQueryStringValue(value);
  if (!serialized) {
    return [];
  }

  return serialized
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function toCsvValue(values: string[]): string | undefined {
  const deduped = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
  return deduped.length ? deduped.join(',') : undefined;
}

function parseOrderParam(value: unknown): { orderBy: InventoryOrderBy; orderDirection: InventoryOrderDirection } {
  if (typeof value !== 'string' || !value.trim()) {
    return { orderBy: 'default', orderDirection: 'asc' };
  }

  const raw = value.trim();
  const orderDirection: InventoryOrderDirection = raw.startsWith('-') ? 'desc' : 'asc';
  const normalized = raw.replace(/^[-+]/, '');

  if (normalized === 'name' || normalized === 'priority' || normalized === 'lastCrawledAt' || normalized === 'healthScore') {
    return { orderBy: normalized, orderDirection };
  }

  return { orderBy: 'default', orderDirection: 'asc' };
}

function toOrderParam(orderBy: InventoryOrderBy, orderDirection: InventoryOrderDirection): string | undefined {
  if (orderBy === 'default') {
    return undefined;
  }

  const field = orderBy === 'lastCrawledAt' ? 'lastCrawledAt' : orderBy;
  return orderDirection === 'desc' ? `-${field}` : field;
}

function termLabelFromKey(value: string): string {
  const term = value.split('.').pop() || value;
  return term.replace(/[-_]+/g, ' ');
}

function buildFilterValuesFromQuery(params: {
  typeValue?: ContentRepresentation;
  statusValues: string[];
  priorityValues: string[];
  taxonomyTermValues: string[];
}): IInventoryFilterValue[] {
  const filters: IInventoryFilterValue[] = [];

  // The list is narrowed to one type at a time, so a link names one.
  if (params.typeValue) {
    filters.push({
      targetId: 'type',
      targetLabel: 'Type',
      targetType: 'type',
      valueId: params.typeValue,
      valueLabel: SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('ContentRepresentation', params.typeValue),
    });
  }

  for (const value of params.statusValues) {
    filters.push({
      targetId: 'status',
      targetLabel: 'Status',
      targetType: 'status',
      valueId: value,
      valueLabel: SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('ContentItemStatus', value as ContentItemStatus),
    });
  }

  for (const value of params.priorityValues) {
    filters.push({
      targetId: 'priority',
      targetLabel: 'Priority',
      targetType: 'priority',
      valueId: value,
      valueLabel: SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('ContentPriority', value as ContentPriority),
    });
  }

  for (const value of params.taxonomyTermValues) {
    const facet = parseFacetFromTaxonomyKey(value);
    filters.push({
      targetId: `taxonomy:${facet || 'unknown'}`,
      targetLabel: facet || 'Taxonomy',
      targetType: 'taxonomy',
      valueId: value,
      valueLabel: termLabelFromKey(value),
    });
  }

  return filters;
}

type IInventoryQuery = {
  orderBy: InventoryOrderBy;
  orderDirection: InventoryOrderDirection;
  selectedFilters: IInventoryFilterValue[];
  taxonomyColumns: TaxonomyFacet[];
  systemColumns: ISystemColumn[];
  search: string;
  pageSize: number;
  hasColumnParams: boolean;
};

function readQueryParamFromSource(source: unknown, key: string): unknown {
  if (!source) {
    return undefined;
  }

  if (source instanceof URLSearchParams) {
    const values = source.getAll(key).filter((entry) => entry.trim().length > 0);
    if (!values.length) {
      return undefined;
    }

    return values.length === 1 ? values[0] : values;
  }

  if (typeof source === 'string') {
    const params = new URLSearchParams(source.replace(/^\?/, ''));
    const values = params.getAll(key).filter((entry) => entry.trim().length > 0);
    if (!values.length) {
      return undefined;
    }

    return values.length === 1 ? values[0] : values;
  }

  if (Array.isArray(source)) {
    for (const entry of source) {
      const value = readQueryParamFromSource(entry, key);
      if (value !== undefined) {
        return value;
      }
    }

    return undefined;
  }

  if (typeof source === 'object') {
    const record = source as Record<string, unknown>;
    const direct = record[key];
    if (direct !== undefined && direct !== null && !(typeof direct === 'string' && !direct.trim())) {
      return direct;
    }

    if (record.query !== undefined) {
      const nested = readQueryParamFromSource(record.query, key);
      if (nested !== undefined) {
        return nested;
      }
    }
  }

  return undefined;
}

/**
 * The filters as the host page's address carries them. Only that address is read: the frame's own
 * `location.search` is what a link put there once and nothing rewrites, so a filter removed from the
 * host page's address would come back from that stale copy on the next parse.
 */
function parseInventoryQuery(query: unknown, brandPresencePath?: string): IInventoryQuery {
  const pathParams = new URLSearchParams(brandPresencePath?.split('?')[1] || '');

  const readParam = (key: string): unknown => {
    const fromQuery = readQueryParamFromSource(query, key);
    if (fromQuery !== undefined) {
      return fromQuery;
    }

    const fromPath = pathParams.get(key);
    if (fromPath !== null && fromPath.trim()) {
      return fromPath;
    }

    return undefined;
  };

  const orderRaw = readParam(QUERY_ORDER_PARAM);
  const pageSizeRaw = readParam(QUERY_PAGE_SIZE_PARAM);
  const columnsRaw = readParam(QUERY_COLUMNS_PARAM);
  const statusRaw = readParam(QUERY_STATUS_PARAM);
  const priorityRaw = readParam(QUERY_PRIORITY_PARAM);
  const termsRaw = readParam(QUERY_TERMS_PARAM);
  const typeRaw = readParam(QUERY_TYPE_PARAM);
  const searchRaw = readParam(QUERY_SEARCH_PARAM);

  const columnsValues = parseCsvValue(columnsRaw);
  const hasColumnParams = columnsValues.length > 0;
  const facets = new Set(Object.values(TaxonomyFacet) as string[]);

  const taxonomyColumns = columnsValues.filter((value): value is TaxonomyFacet => facets.has(value));
  const systemColumnsFromQuery = columnsValues.filter(
    (value): value is ISystemColumn =>
      value === 'score' || value === 'status' || value === 'worstChecks' || value === 'priority' || value === 'locale'
  );
  const systemColumns = hasColumnParams ? systemColumnsFromQuery : DEFAULT_VISIBLE_SYSTEM_COLUMNS;

  const statusValues = parseCsvValue(statusRaw);
  const priorityValues = parseCsvValue(priorityRaw);
  const taxonomyTermValues = parseCsvValue(termsRaw).filter(isTaxonomyTermKey);
  const typeValue = parseCsvValue(typeRaw)
    .map(toContentType)
    .find((value) => value !== undefined);
  const hasAnyFilterValues = typeValue || statusValues.length || priorityValues.length || taxonomyTermValues.length;

  const selectedFilters = hasAnyFilterValues
    ? buildFilterValuesFromQuery({ typeValue, statusValues, priorityValues, taxonomyTermValues })
    : [getDefaultPublishedFilter()];

  const parsedOrder = parseOrderParam(orderRaw);

  return {
    selectedFilters,
    orderBy: parsedOrder.orderBy,
    orderDirection: parsedOrder.orderDirection,
    taxonomyColumns,
    systemColumns,
    search: coerceQueryStringValue(searchRaw) || '',
    pageSize: parsePageSizeToken(coerceQueryStringValue(pageSizeRaw)),
    hasColumnParams,
  };
}

function buildUrlQueryFromState(params: {
  orderBy: InventoryOrderBy;
  orderDirection: InventoryOrderDirection;
  selectedFilters: IInventoryFilterValue[];
  selectedSystemColumns: ISystemColumn[];
  selectedTaxonomyColumns: TaxonomyFacet[];
  search: string;
  pageSize: number;
}): Record<string, string> {
  const typeValue = params.selectedFilters.find((filter) => filter.targetType === 'type')?.valueId;
  const statusValues = params.selectedFilters.filter((filter) => filter.targetType === 'status').map((filter) => filter.valueId);
  const priorityValues = params.selectedFilters.filter((filter) => filter.targetType === 'priority').map((filter) => filter.valueId);
  const taxonomyTermValues = params.selectedFilters
    .filter((filter) => filter.targetType === 'taxonomy' && isTaxonomyTermKey(filter.valueId))
    .map((filter) => filter.valueId);

  const columns = toCsvValue([...FIXED_COLUMNS, ...params.selectedSystemColumns, ...params.selectedTaxonomyColumns]);

  return {
    ...(toOrderParam(params.orderBy, params.orderDirection)
      ? { [QUERY_ORDER_PARAM]: toOrderParam(params.orderBy, params.orderDirection) as string }
      : {}),
    ...(columns ? { [QUERY_COLUMNS_PARAM]: columns } : {}),
    ...(typeValue ? { [QUERY_TYPE_PARAM]: typeValue } : {}),
    ...(toCsvValue(statusValues) ? { [QUERY_STATUS_PARAM]: toCsvValue(statusValues) as string } : {}),
    ...(toCsvValue(priorityValues) ? { [QUERY_PRIORITY_PARAM]: toCsvValue(priorityValues) as string } : {}),
    ...(toCsvValue(taxonomyTermValues) ? { [QUERY_TERMS_PARAM]: toCsvValue(taxonomyTermValues) as string } : {}),
    ...(params.search.trim() ? { [QUERY_SEARCH_PARAM]: params.search.trim() } : {}),
    ...(pageSizeToToken(params.pageSize) ? { [QUERY_PAGE_SIZE_PARAM]: pageSizeToToken(params.pageSize) as string } : {}),
  };
}

function parseFacetFromTaxonomyKey(key: string): TaxonomyFacet | undefined {
  const facets = new Set((Object.values(TaxonomyFacet) as string[]).map((facet) => facet.trim()).filter(Boolean));
  const segments = key
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean);

  for (const segment of segments) {
    if (facets.has(segment)) {
      return segment as TaxonomyFacet;
    }
  }

  return undefined;
}

function toScore(item: ClientContentItemEntity) {
  if (item.healthScore === undefined || !Number.isFinite(item.healthScore)) {
    return undefined;
  }

  const normalizedScore = Math.min(1, Math.max(0, item.healthScore));
  return Math.round(normalizedScore * 100);
}

function toWorstHealthChecks(item: ClientContentItemEntity): WorstCheckModel[] {
  const healthEntries = item.health || [];

  const checks = healthEntries
    .map((healthEntry): { score?: number; healthCheckKey?: string } => {
      const score = healthEntry.score;
      const healthCheckKey = healthEntry.healthCheck.getStableKey();

      return {
        score: typeof score === 'number' && Number.isFinite(score) ? score : undefined,
        healthCheckKey,
      };
    })
    .filter((entry): entry is WorstCheckModel => entry.score !== undefined && !!entry.healthCheckKey && entry.score < 0.9)
    .sort((left, right) => left.score - right.score);

  return checks;
}

function toTaxonomyValuesByFacet(
  assignments: IRuntimeTermAssignment[],
  taxonomyIdsByFacet: Map<TaxonomyFacet, string>
): Partial<Record<TaxonomyFacet, string[]>> {
  const valuesByFacet: Partial<Record<TaxonomyFacet, string[]>> = {};

  for (const [facet, taxonomyId] of taxonomyIdsByFacet.entries()) {
    const termKeys = assignments
      .filter((assignment) => String(assignment.taxonomy.getId()) === taxonomyId)
      .map((assignment) => assignment.term.getStableKey());

    const uniqueTermKeys = Array.from(new Set(termKeys.map((termKey) => termKey.trim()).filter((termKey) => termKey.length > 0)));

    if (uniqueTermKeys.length) {
      valuesByFacet[facet] = uniqueTermKeys;
    }
  }

  return valuesByFacet;
}

function toPriorityLabel(item: ClientContentItemEntity): string {
  const source = item as { contentPriority?: ContentPriority; priority?: ContentPriority };
  const priority = source.contentPriority ?? source.priority;

  if (!priority) {
    return '-';
  }

  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('ContentPriority', priority);
}

function formatAgeFromNow(value?: { valueOf: () => number }): string {
  if (!value) {
    return '-';
  }

  const timestamp = value.valueOf();
  if (!Number.isFinite(timestamp)) {
    return '-';
  }

  const diffMs = Math.max(0, Date.now() - timestamp);

  const units = [
    { label: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
    { label: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
    { label: 'week', ms: 7 * 24 * 60 * 60 * 1000 },
    { label: 'day', ms: 24 * 60 * 60 * 1000 },
    { label: 'hour', ms: 60 * 60 * 1000 },
    { label: 'minute', ms: 60 * 1000 },
    { label: 'second', ms: 1000 },
  ];

  for (const unit of units) {
    if (diffMs >= unit.ms) {
      const amount = Math.floor(diffMs / unit.ms);
      return `${amount} ${unit.label}${amount === 1 ? '' : 's'}`;
    }
  }

  return '0 seconds';
}

function toRow(item: ClientContentItemEntity, taxonomyIdsByFacet: Map<TaxonomyFacet, string>): RowModel | undefined {
  const itemName = item.name?.trim();
  if (!itemName) {
    return undefined;
  }

  const score = toScore(item);
  const worstChecks = toWorstHealthChecks(item);
  const assignedTerms: IRuntimeTermAssignment[] = item.terms?.assigned || [];

  return {
    id: item.id,
    key: item.key,
    name: itemName,
    score,
    contentStatus: item.status as ContentItemStatus,
    worstChecks,
    taxonomyValuesByFacet: toTaxonomyValuesByFacet(assignedTerms, taxonomyIdsByFacet),
    priority: toPriorityLabel(item),
    age: formatAgeFromNow(item.lastCrawledAt),
    url: item.url,
  };
}

function inferCrawlProgress(summary: TaskGroupSummaryItem[] | undefined): CrawlProgress {
  if (!summary?.length) {
    return {
      progress: 8,
      title: 'Crawl in progress',
      finished: false,
    };
  }

  const total = summary.reduce((sum, item) => sum + (item.count || 0), 0);

  if (total <= 0) {
    return {
      progress: 8,
      title: 'Crawl in progress',
      finished: false,
    };
  }

  const doneStatusKeywords = ['finished', 'failed', 'aborted', 'skipped', 'limitexceeded'];
  const doneCount = summary.reduce((sum, item) => {
    const normalized = String(item.status || '').toLowerCase();
    const done = doneStatusKeywords.some((keyword) => normalized.includes(keyword));
    return sum + (done ? item.count || 0 : 0);
  }, 0);

  const rawProgress = Math.round((doneCount / total) * 100);
  const finished = doneCount >= total;
  const progress = finished ? 100 : Math.max(8, Math.min(98, rawProgress));

  return {
    progress,
    title: `Crawl in progress: ${doneCount} of ${total} items processed`,
    finished,
  };
}

export function GovernanceContentInventory() {
  const apiContext = useContext(SyncCoreApiContext);
  const api = apiContext?.api;
  const {
    isIndexingDeferred,
    project,
    isProjectReadFailed,
    failedRecordingMode,
    isRecording,
    recordOnboardingCompletion,
    dismissFailedRecording,
    retry,
  } = useBrandPresenceOnboarding();
  const navigate = useNavigate();

  const { query: requestedQuery, path: requestedPath } = useBrandPresenceScreenAddress();
  const parsedInventoryQuery = useMemo(() => parseInventoryQuery(requestedQuery, requestedPath), [requestedQuery, requestedPath]);
  const parsedInventoryQuerySerialized = useMemo(() => JSON.stringify(parsedInventoryQuery), [parsedInventoryQuery]);
  const lastSentQuerySerializedRef = React.useRef<string | undefined>(undefined);
  const isHydratedFromIncomingRef = React.useRef(false);
  const initialHydratedQuerySerializedRef = React.useRef<string | undefined>(undefined);
  const hasQueryDrivenColumns = parsedInventoryQuery.hasColumnParams;

  const [loading, setLoading] = useState(true);
  // Only the first load draws the whole page as a skeleton; later loads keep the page and reload the rows.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [items, setItems] = useState<ClientContentItemEntity[]>([]);
  const [searchInput, setSearchInput] = useState(parsedInventoryQuery.search);
  const [search, setSearch] = useState(parsedInventoryQuery.search);
  const [availableTaxonomiesByFacet, setAvailableTaxonomiesByFacet] = useState<Map<TaxonomyFacet, string>>(new Map());
  const [taxonomyIdsByFacet, setTaxonomyIdsByFacet] = useState<Map<TaxonomyFacet, string>>(new Map());
  const [selectedFilters, setSelectedFilters] = useState<IInventoryFilterValue[]>(() => parsedInventoryQuery.selectedFilters);
  const [selectedTaxonomyColumns, setSelectedTaxonomyColumns] = useState<TaxonomyFacet[]>(() => parsedInventoryQuery.taxonomyColumns);
  const [selectedSystemColumns, setSelectedSystemColumns] = useState<ISystemColumn[]>(() => parsedInventoryQuery.systemColumns);
  const [orderBy, setOrderBy] = useState<InventoryOrderBy>(() => parsedInventoryQuery.orderBy);
  const [pageSize, setPageSize] = useState<number>(() => parsedInventoryQuery.pageSize);
  const [orderDirection, setOrderDirection] = useState<InventoryOrderDirection>(() => parsedInventoryQuery.orderDirection);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedWorstCheckRows, setExpandedWorstCheckRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(1);
  const [totalNumberOfItems, setTotalNumberOfItems] = useState(0);
  const [crawlProgress, setCrawlProgress] = useState<CrawlProgress | undefined>(undefined);
  const [listRefreshNonce, setListRefreshNonce] = useState(0);
  const [averageHealthScore, setAverageHealthScore] = useState<number | undefined>(undefined);
  const [lowHealthCount, setLowHealthCount] = useState(0);
  const [worstCheck, setWorstCheck] = useState<WorstCheckModel | undefined>(undefined);
  const [selectionAction, setSelectionAction] = useState<ContentItemBulkAction | undefined>(undefined);
  // An emptied selection closes the action bar; the next selection starts from a fresh choice.
  useEffect(() => {
    if (!selectedIds.size) {
      setSelectionAction(undefined);
    }
  }, [selectedIds]);
  const selectedFiltersSerialized = useMemo(() => serializeFiltersParam(selectedFilters), [selectedFilters]);
  const selectedTaxonomyColumnsSerialized = useMemo(() => JSON.stringify(selectedTaxonomyColumns), [selectedTaxonomyColumns]);
  const selectedSystemColumnsSerialized = useMemo(() => JSON.stringify(selectedSystemColumns), [selectedSystemColumns]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
    }, BRAND_PRESENCE_LIST_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const parsedFiltersSerialized = serializeFiltersParam(parsedInventoryQuery.selectedFilters);

    if (parsedFiltersSerialized !== selectedFiltersSerialized) {
      setSelectedFilters(parsedInventoryQuery.selectedFilters);
    }

    if (parsedInventoryQuery.orderBy !== orderBy) {
      setOrderBy(parsedInventoryQuery.orderBy);
    }

    if (parsedInventoryQuery.orderDirection !== orderDirection) {
      setOrderDirection(parsedInventoryQuery.orderDirection);
    }

    if (parsedInventoryQuery.pageSize !== pageSize) {
      setPageSize(parsedInventoryQuery.pageSize);
    }

    const parsedTaxonomyColumnsSerialized = JSON.stringify(parsedInventoryQuery.taxonomyColumns);
    if (parsedTaxonomyColumnsSerialized !== selectedTaxonomyColumnsSerialized) {
      setSelectedTaxonomyColumns(parsedInventoryQuery.taxonomyColumns);
    }

    const parsedSystemColumnsSerialized = JSON.stringify(parsedInventoryQuery.systemColumns);
    if (parsedSystemColumnsSerialized !== selectedSystemColumnsSerialized) {
      setSelectedSystemColumns(parsedInventoryQuery.systemColumns);
    }

    if (parsedInventoryQuery.search !== searchInput) {
      setSearchInput(parsedInventoryQuery.search);
    }

    if (parsedInventoryQuery.search !== search) {
      setSearch(parsedInventoryQuery.search);
    }

    const hydratedQuery = buildUrlQueryFromState({
      orderBy: parsedInventoryQuery.orderBy,
      orderDirection: parsedInventoryQuery.orderDirection,
      selectedFilters: parsedInventoryQuery.selectedFilters,
      selectedSystemColumns: parsedInventoryQuery.systemColumns,
      selectedTaxonomyColumns: parsedInventoryQuery.taxonomyColumns,
      search: parsedInventoryQuery.search,
      pageSize: parsedInventoryQuery.pageSize,
    });
    const hydratedSerialized = JSON.stringify(hydratedQuery);
    initialHydratedQuerySerializedRef.current = hydratedSerialized;
    lastSentQuerySerializedRef.current = hydratedSerialized;
    isHydratedFromIncomingRef.current = true;
  }, [parsedInventoryQuerySerialized]);

  useEffect(() => {
    if (!isHydratedFromIncomingRef.current) {
      return;
    }

    const nextQuery = buildUrlQueryFromState({
      orderBy,
      orderDirection,
      selectedFilters,
      selectedSystemColumns,
      selectedTaxonomyColumns,
      search,
      pageSize,
    });

    const nextSerialized = JSON.stringify(nextQuery);

    if (initialHydratedQuerySerializedRef.current === nextSerialized && lastSentQuerySerializedRef.current === nextSerialized) {
      return;
    }

    if (lastSentQuerySerializedRef.current === nextSerialized) {
      return;
    }

    lastSentQuerySerializedRef.current = nextSerialized;
    sendToParent({
      type: 'update-query',
      query: nextQuery,
    });
  }, [
    orderBy,
    orderDirection,
    search,
    pageSize,
    selectedFiltersSerialized,
    selectedTaxonomyColumnsSerialized,
    selectedSystemColumnsSerialized,
  ]);

  const screenQuery = useMemo(
    () =>
      buildUrlQueryFromState({
        orderBy,
        orderDirection,
        selectedFilters,
        selectedSystemColumns,
        selectedTaxonomyColumns,
        search,
        pageSize,
      }),
    [orderBy, orderDirection, search, pageSize, selectedFilters, selectedSystemColumns, selectedTaxonomyColumns]
  );
  useReportBrandPresenceScreenQuery(screenQuery);

  useEffect(() => {
    setPage(0);
  }, [orderBy, orderDirection, search, pageSize, selectedFiltersSerialized]);

  useEffect(() => {
    if (!api) {
      setLoading(false);
      return;
    }

    let active = true;
    void (async () => {
      setLoading(true);
      const listFilters = buildContentItemListFilters(selectedFilters, search, orderBy, orderDirection);
      const result = await api.governance.contentItems.list({ page, itemsPerPage: pageSize }, listFilters);
      const taxonomies = await getCachedTaxonomies(api);
      if (!active) {
        return;
      }

      const loadedItems = result.items || [];
      const taxonomyByFacet = new Map<TaxonomyFacet, string>();
      const taxonomyIdByFacet = new Map<TaxonomyFacet, string>();
      for (const taxonomy of taxonomies) {
        if (!taxonomy.facet) {
          continue;
        }

        const facet = taxonomy.facet as TaxonomyFacet;
        if (!taxonomyByFacet.has(facet)) {
          taxonomyByFacet.set(facet, taxonomy.name);
        }

        if (!taxonomyIdByFacet.has(facet)) {
          taxonomyIdByFacet.set(facet, taxonomy.id);
        }
      }

      setItems(loadedItems);
      setNumberOfPages(Math.max(1, result.numberOfPages || 0));
      setTotalNumberOfItems(result.totalNumberOfItems ?? 0);
      setAvailableTaxonomiesByFacet(taxonomyByFacet);
      setTaxonomyIdsByFacet(taxonomyIdByFacet);
      setAverageHealthScore(result.averageHealthScore === null ? undefined : result.averageHealthScore);
      setLowHealthCount(result.lowHealthCount ?? 0);
      setWorstCheck(
        result.worstCheck
          ? {
              healthCheckKey: result.worstCheck.key,
              score: result.worstCheck.averageScore,
            }
          : undefined
      );
      setSelectedIds(new Set());
      setExpandedWorstCheckRows(new Set());
      setLoading(false);
      setHasLoadedOnce(true);
    })();

    return () => {
      active = false;
    };
  }, [api, orderBy, orderDirection, search, selectedFilters, page, pageSize, listRefreshNonce]);

  useEffect(() => {
    if (!api) {
      setCrawlProgress(undefined);
      return;
    }

    let active = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const refreshCrawlTaskGroupSummary = async (taskGroupId: InternalId) => {
      const summary = await api.service.taskGroups.summary(taskGroupId);
      if (!active) {
        return;
      }

      const crawlProgressSummary = inferCrawlProgress((summary.byStatus || []) as TaskGroupSummaryItem[]);
      if (crawlProgressSummary.finished) {
        setCrawlProgress(undefined);
        return;
      }

      setCrawlProgress(crawlProgressSummary);
      timeout = setTimeout(() => {
        void refreshCrawlTaskGroupSummary(taskGroupId);
      }, 10_000);
    };

    void (async () => {
      const latestCrawlTaskGroups = await api.service.taskGroups.list(
        {
          domains: [TaskDomain.Governance],
          types: [TaskGroupType.Crawl],
        },
        {
          page: 0,
          itemsPerPage: 1,
        }
      );

      if (!active) {
        return;
      }

      const latestTaskGroup = latestCrawlTaskGroups.items?.[0];
      if (!latestTaskGroup) {
        setCrawlProgress(undefined);
        return;
      }

      await refreshCrawlTaskGroupSummary(latestTaskGroup.id as InternalId);
    })();

    return () => {
      active = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [api]);

  const rows = useMemo(
    () => items.map((item) => toRow(item, taxonomyIdsByFacet)).filter((row): row is RowModel => !!row),
    [items, taxonomyIdsByFacet]
  );
  const selectedItems = useMemo(() => items.filter((item) => selectedIds.has(item.id)), [items, selectedIds]);
  const issueTypeTaxonomyId = taxonomyIdsByFacet.get(TaxonomyFacet.IssueType);

  const applyOrder = (nextOrderBy: SortableInventoryOrderBy) => {
    if (orderBy === nextOrderBy) {
      setOrderDirection((value) => (value === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setOrderBy(nextOrderBy);
    setOrderDirection(ORDER_DEFAULT_DIRECTION[nextOrderBy]);
  };

  useEffect(() => {
    if (!rows.length || !availableTaxonomiesByFacet.size || selectedTaxonomyColumns.length || hasQueryDrivenColumns) {
      return;
    }

    const defaults = DEFAULT_TAXONOMY_COLUMNS.filter((facet) => availableTaxonomiesByFacet.has(facet));

    setSelectedTaxonomyColumns(defaults);
  }, [availableTaxonomiesByFacet, rows, selectedTaxonomyColumns.length]);

  const safePage = Math.min(page, numberOfPages - 1);
  const pagedRows = rows;

  const allSelected = pagedRows.length > 0 && pagedRows.every((row) => selectedIds.has(row.id));
  const worstCheckScorePercentage = worstCheck ? Math.round(Math.max(0, Math.min(1, worstCheck.score)) * 100) : undefined;

  useEffect(() => {
    if (page > numberOfPages - 1) {
      setPage(Math.max(0, numberOfPages - 1));
    }
  }, [numberOfPages, page]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pagedRows.forEach((row) => next.delete(row.id));
        return next;
      });
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      pagedRows.forEach((row) => next.add(row.id));
      return next;
    });
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  /**
   * A page's own screen, keeping the list's filters so Back returns to the same view. The filters
   * as they stand now, not the frame's own address: that address never carries them, because the
   * changes travel to the host page through `update-query`.
   */
  const itemDetailPath = ({ key, status }: { key: string; status?: ContentItemStatus }) =>
    `${brandPresenceContentItemPath({ key, status })}${toBrandPresenceSearch(screenQuery)}`;

  const navigateToItemDetail = ({ key, status }: { key: string; status?: ContentItemStatus }) => {
    navigate(itemDetailPath({ key, status }));
  };

  const shouldSkipRowNavigation = (target: EventTarget | null): boolean => {
    const element = target as Element | null;
    return !!element?.closest('a, button, input, select, textarea, label, [role="button"], [data-no-row-navigation="true"]');
  };

  // Finishing the wizard without starting automatic indexing leaves it off for this project. The
  // reminder is owed until automatic page crawling is on, and it is the crawling settings that turn
  // it on again — nothing clears the recorded choice, so the reminder follows what the settings say
  // rather than the choice made once. It reads the project the area resolved, so it stays hidden
  // until that read is in: a read that failed shows its own notice instead of this reminder.
  const isAutomaticPageCrawlingOff = !!project && !(project.crawling?.autoCrawl || []).includes(ContentRepresentation.Page);
  const showDeferredIndexingNotice = isIndexingDeferred && isAutomaticPageCrawlingOff;

  const columnCount =
    // Checkbox, name, age and the action are always there; the rest follow the reader's column choice.
    4 +
    (['status', 'priority', 'locale', 'score', 'worstChecks'] as ISystemColumn[]).filter((column) => selectedSystemColumns.includes(column))
      .length +
    selectedTaxonomyColumns.length;

  if (loading && !hasLoadedOnce) {
    return <ContentInventorySkeleton columnCount={columnCount} />;
  }

  const isReloading = loading;
  const hasNarrowedList = !!search.trim() || selectedFiltersSerialized !== DEFAULT_FILTERS_SERIALIZED;
  const riskShare = totalNumberOfItems ? Math.round((lowHealthCount / totalNumberOfItems) * 100) : undefined;

  return (
    <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
      {crawlProgress ? <ProgressBar size="hairline" percent={crawlProgress.progress} title={crawlProgress.title} /> : null}
      <main className="relative max-w-[1800px] min-h-[800px] mx-auto px-6 py-8">
        <BrandPresencePageHeader
          eyebrow={
            <>
              Brand Presence <span className="text-slate-300">/</span>{' '}
              <span className="tabular-nums">
                {totalNumberOfItems} {totalNumberOfItems === 1 ? 'page' : 'pages'}
              </span>
            </>
          }
          title="Content Inventory"
          description="Every page we track on your site, with its health and the tags that describe it."
        />

        <div className="space-y-3 empty:hidden mb-6">
          {failedRecordingMode ? (
            <Alert variant="warning" dismissible onClose={dismissFailedRecording} closeLabel="Dismiss this message">
              <div className="d-flex align-items-center flex-wrap gap-3">
                <span>We could not save everything about how you finished setting up. Try again so this project has it on record.</span>
                {/* Never disabled: disabling the button a user just pressed takes the focus off it. A
                    second press while the first is in flight is ignored where the write is issued. */}
                <Button variant="outline-secondary" size="sm" onClick={() => recordOnboardingCompletion(failedRecordingMode)}>
                  {isRecording ? 'Saving...' : 'Try again'}
                </Button>
              </div>
            </Alert>
          ) : null}
          {isProjectReadFailed ? (
            <Alert variant="warning">
              <div className="d-flex align-items-center flex-wrap gap-3">
                <span>
                  We could not load this project&apos;s indexing settings, so we cannot tell you whether indexing is on. Try again.
                </span>
                <Button variant="outline-secondary" size="sm" onClick={retry}>
                  Try again
                </Button>
              </div>
            </Alert>
          ) : null}
          {showDeferredIndexingNotice ? (
            <Alert variant="info">
              <div className="d-flex align-items-center flex-wrap gap-3">
                <span>Automatic page indexing is off, so new pages will not be added here until you switch it on.</span>
                <ButtonLink to={BRAND_PRESENCE_CRAWLING_SETTINGS_PATH} variant="outline">
                  Open crawling settings
                </ButtonLink>
              </div>
            </Alert>
          ) : null}
        </div>

        <GovernanceAnalyticsKpiCardRow className="mb-8">
          <GovernanceAnalyticsKpiCard
            title="Average Health Score"
            value={averageHealthScore !== undefined ? `${Math.round(averageHealthScore * 100)}%` : EM_DASH}
            subtitle="Across every page in this list"
          >
            {averageHealthScore !== undefined ? <KpiMeter fraction={averageHealthScore} label="Average health score" /> : null}
          </GovernanceAnalyticsKpiCard>
          <GovernanceAnalyticsKpiCard
            title="Content at Risk"
            value={String(lowHealthCount)}
            valueSuffix={`/ ${totalNumberOfItems}`}
            subtitle={riskShare !== undefined ? `${riskShare}% of your pages need attention` : undefined}
          >
            {riskShare !== undefined ? (
              <KpiMeter fraction={riskShare / 100} label="Share of pages at risk" fillClassName="bg-brand-coral" />
            ) : null}
          </GovernanceAnalyticsKpiCard>
          <GovernanceAnalyticsKpiCard
            title="Worst Health Check Score"
            value={worstCheckScorePercentage !== undefined ? `${worstCheckScorePercentage}%` : EM_DASH}
            adornment={
              worstCheck ? (
                <HealthCheckCriticalityBadge api={api} healthCheckKey={worstCheck.healthCheckKey} score={worstCheck.score} />
              ) : null
            }
            subtitle={worstCheck ? <HealthCheckNameReference api={api} healthCheckKey={worstCheck.healthCheckKey} /> : undefined}
          >
            {worstCheckScorePercentage !== undefined ? (
              <KpiMeter
                fraction={worstCheckScorePercentage / 100}
                label="Worst health check score"
                fillClassName={HEALTH_METER_FILL_CLASSES[resolveHealthCheckScorePresentation(worstCheckScorePercentage).label]}
              />
            ) : null}
          </GovernanceAnalyticsKpiCard>
        </GovernanceAnalyticsKpiCardRow>

        {/* No overflow clipping on the card: the toolbar's menus open below it and must stay whole. */}
        <section className={`${BP_CARD_CLASSES} motion-safe:animate-bp-fade-up [--bp-delay:180ms]`} aria-label="Pages">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/70 px-4 py-3">
            <div className="flex flex-1 flex-wrap items-center gap-3">
              <div className="relative w-72 max-w-full">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-xs text-slate-400" />
                </span>
                <input
                  className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50/70 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 transition-all duration-150 focus:border-primary focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary/10"
                  placeholder="Search content..."
                  aria-label="Search content"
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>

              <AddFilterDropdown api={api} selectedFilters={selectedFilters} onChange={setSelectedFilters} />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={BP_TOOLBAR_BUTTON_CLASSES}
                onClick={() => setListRefreshNonce((prev) => prev + 1)}
                title="Refresh list"
                disabled={isReloading}
              >
                <FontAwesomeIcon icon={faArrowsRotate} className={`text-xs ${isReloading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <ColumnPicker
                api={api}
                selectedFacets={selectedTaxonomyColumns}
                selectedSystemColumns={selectedSystemColumns}
                onToggleFacet={(facet) => {
                  setSelectedTaxonomyColumns((prev) => {
                    if (prev.includes(facet)) {
                      return prev.filter((entry) => entry !== facet);
                    }
                    return [...prev, facet];
                  });
                }}
                onToggleSystemColumn={(column) => {
                  setSelectedSystemColumns((prev) => {
                    if (prev.includes(column)) {
                      return prev.filter((entry) => entry !== column);
                    }
                    return [...prev, column];
                  });
                }}
              />
            </div>
          </div>

          {selectedIds.size > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-primary/15 bg-primary/[0.05] px-4 py-2.5 motion-safe:animate-bp-slide-down">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-white tabular-nums">
                  {selectedIds.size}
                </span>
                {selectedIds.size === 1 ? 'item selected' : 'items selected'}
              </div>
              <div className="flex flex-wrap items-start gap-2">
                <ContentItemActionDropdown
                  api={api}
                  selectedItems={selectedItems}
                  issueTypeTaxonomyId={issueTypeTaxonomyId}
                  action={selectionAction}
                  onActionChange={setSelectionAction}
                />
                {selectionAction?.kind === 'add-terms' || selectionAction?.kind === 'remove-terms' ? (
                  // Keyed by the action, so switching between adding and removing starts from no tags.
                  <ManageTermsActionDropdown
                    key={selectionAction.kind}
                    api={api}
                    selectedItems={selectedItems}
                    mode={selectionAction.kind === 'add-terms' ? 'add' : 'remove'}
                    onExecuted={() => {
                      setSelectionAction(undefined);
                      setListRefreshNonce((previous) => previous + 1);
                    }}
                  />
                ) : null}
                {selectionAction?.kind === 'optimize' ? (
                  <button
                    type="button"
                    className={`${BP_PRIMARY_BUTTON_CLASSES} motion-safe:animate-bp-fade-in`}
                    onClick={() => {
                      const selectedKeys = selectedItems.map((item) => item.key).filter((key) => key.trim().length > 0);
                      const query = new URLSearchParams();

                      if (selectedKeys.length) {
                        query.set('keys', selectedKeys.join(','));
                      }

                      const querySuffix = query.toString();
                      const route = `/brand-presence/optimize/${encodeURIComponent(selectionAction.typeKey)}${
                        querySuffix ? `?${querySuffix}` : ''
                      }`;
                      navigate(route);
                    }}
                  >
                    Optimize {selectedItems.length === 1 ? '1 page' : `${selectedItems.length} pages`}
                  </button>
                ) : null}
                {!selectionAction ? (
                  <button type="button" className={BP_PRIMARY_BUTTON_CLASSES} disabled>
                    Apply
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          <IndeterminateProgress active={isReloading} label="Loading pages" />

          <div className="overflow-x-auto" aria-busy={isReloading}>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/70">
                  <th className={`${BP_TABLE_HEAD_CELL_CLASSES} w-12`}>
                    <Form.Check
                      type="checkbox"
                      className="mb-0"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select every item on this page"
                    />
                  </th>
                  <th className={BP_TABLE_HEAD_CELL_CLASSES}>
                    <GovernanceSortableHeader
                      label="Name"
                      isActive={orderBy === 'name'}
                      direction={orderDirection}
                      onClick={() => applyOrder('name')}
                    />
                  </th>
                  {selectedSystemColumns.includes('status') ? <th className={`${BP_TABLE_HEAD_CELL_CLASSES} w-24`}>Status</th> : null}
                  {selectedSystemColumns.includes('priority') ? (
                    <th className={`${BP_TABLE_HEAD_CELL_CLASSES} min-w-[120px]`}>
                      <GovernanceSortableHeader
                        label="Priority"
                        isActive={orderBy === 'priority'}
                        direction={orderDirection}
                        onClick={() => applyOrder('priority')}
                      />
                    </th>
                  ) : null}
                  {selectedSystemColumns.includes('locale') ? (
                    <th className={`${BP_TABLE_HEAD_CELL_CLASSES} min-w-[160px]`}>Locale</th>
                  ) : null}
                  {selectedSystemColumns.includes('score') ? (
                    <th className={`${BP_TABLE_HEAD_CELL_CLASSES} w-28`}>
                      <GovernanceSortableHeader
                        label="Health"
                        isActive={orderBy === 'healthScore'}
                        direction={orderDirection}
                        onClick={() => applyOrder('healthScore')}
                      />
                    </th>
                  ) : null}
                  {/* The worst checks keep the width they have on a wide screen: squeezed to the table's
                      narrowest on a phone, their names would break after every word and a row would
                      grow several times its height. */}
                  {selectedSystemColumns.includes('worstChecks') ? (
                    <th className={`${BP_TABLE_HEAD_CELL_CLASSES} min-w-[300px]`}>Worst Checks</th>
                  ) : null}
                  {selectedTaxonomyColumns.map((facet) => (
                    <th key={facet} className={`${BP_TABLE_HEAD_CELL_CLASSES} min-w-[160px]`}>
                      {availableTaxonomiesByFacet.get(facet) || facet}
                    </th>
                  ))}
                  <th className={`${BP_TABLE_HEAD_CELL_CLASSES} w-24`}>
                    <GovernanceSortableHeader
                      label="Age"
                      isActive={orderBy === 'lastCrawledAt'}
                      direction={orderDirection}
                      onClick={() => applyOrder('lastCrawledAt')}
                    />
                  </th>
                  <th className={`${BP_TABLE_HEAD_CELL_CLASSES} text-right w-28`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {isReloading ? (
                  <ContentInventorySkeletonRows rowCount={Math.max(3, Math.min(pagedRows.length, pageSize))} columnCount={columnCount} />
                ) : null}
                {!isReloading && !pagedRows.length ? (
                  <tr>
                    <td colSpan={columnCount}>
                      {hasNarrowedList ? (
                        <BrandPresenceEmptyState
                          icon={faMagnifyingGlass}
                          title="No pages match"
                          description="Nothing in your inventory fits this search and these filters. Try other words or remove a filter."
                          actions={
                            <button
                              type="button"
                              className={BP_TOOLBAR_BUTTON_CLASSES}
                              onClick={() => {
                                setSearchInput('');
                                setSearch('');
                                setSelectedFilters([getDefaultPublishedFilter()]);
                              }}
                            >
                              Reset search and filters
                            </button>
                          }
                        />
                      ) : (
                        <BrandPresenceEmptyState
                          icon={faFileLines}
                          title="No pages yet"
                          description="Pages appear here as soon as your site has been indexed. The crawling settings decide what gets indexed and when."
                          actions={
                            // The indexing reminder above already offers the way there.
                            showDeferredIndexingNotice ? undefined : (
                              <ButtonLink to={BRAND_PRESENCE_CRAWLING_SETTINGS_PATH} variant="outline">
                                Open crawling settings
                              </ButtonLink>
                            )
                          }
                        />
                      )}
                    </td>
                  </tr>
                ) : null}
                {isReloading
                  ? null
                  : pagedRows.map((row, rowIndex) => {
                      const selected = selectedIds.has(row.id);
                      const showAllWorstChecks = expandedWorstCheckRows.has(row.id);
                      const visibleWorstChecks = showAllWorstChecks ? row.worstChecks : row.worstChecks.slice(0, 3);
                      return (
                        <tr
                          key={row.id}
                          className={`group/row align-middle transition-colors duration-150 motion-safe:animate-bp-fade-up ${
                            ROW_ENTRANCE_DELAYS[Math.min(rowIndex, ROW_ENTRANCE_DELAYS.length - 1)]
                          } ${selected ? 'bg-primary/[0.04] hover:bg-primary/[0.07]' : 'hover:bg-slate-50/80'} ${row.key ? 'cursor-pointer' : ''}`}
                          onClick={(event) => {
                            if (!row.key || shouldSkipRowNavigation(event.target)) {
                              return;
                            }

                            navigateToItemDetail({ key: row.key, status: row.contentStatus });
                          }}
                        >
                          <td className="px-4 py-3.5">
                            <Form.Check
                              type="checkbox"
                              className="mb-0"
                              checked={selected}
                              onChange={() => toggleRow(row.id)}
                              aria-label={`Select ${row.name}`}
                            />
                          </td>
                          <td className="px-4 py-3.5 max-w-[300px]">
                            <div className="truncate text-sm font-semibold">
                              {row.key ? (
                                <Link
                                  to={itemDetailPath({ key: row.key, status: row.contentStatus })}
                                  title={row.name}
                                  className="text-slate-900 no-underline transition-colors hover:text-primary hover:no-underline"
                                >
                                  {row.name}
                                </Link>
                              ) : (
                                <span title={row.name}>{row.name}</span>
                              )}
                            </div>
                            {row.url ? (
                              <div className="mt-0.5 truncate font-eyebrow text-[0.6875rem] text-slate-400" title={row.url}>
                                {row.url}
                              </div>
                            ) : null}
                          </td>
                          {selectedSystemColumns.includes('status') ? (
                            <td className="px-4 py-3.5">
                              <ContentItemStatusIcon status={row.contentStatus} />
                            </td>
                          ) : null}
                          {selectedSystemColumns.includes('priority') ? (
                            <td className="px-4 py-3.5 text-sm text-slate-600">{row.priority}</td>
                          ) : null}
                          {selectedSystemColumns.includes('locale') ? (
                            <td className="px-4 py-3.5 text-sm text-slate-500">
                              {row.taxonomyValuesByFacet[TaxonomyFacet.Locale]?.length ? (
                                <div className="flex flex-wrap gap-1">
                                  {row.taxonomyValuesByFacet[TaxonomyFacet.Locale]!.map((termKey) => (
                                    <TaxonomyTermNameBadge
                                      key={`${row.id}:locale:${termKey}`}
                                      api={api}
                                      termKey={termKey}
                                      termFacet={TaxonomyFacet.Locale}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400">{EM_DASH}</span>
                              )}
                            </td>
                          ) : null}
                          {selectedSystemColumns.includes('score') ? (
                            <td className="px-4 py-3.5">
                              <HealthScoreCell score={row.score} />
                            </td>
                          ) : null}
                          {selectedSystemColumns.includes('worstChecks') ? (
                            <td className="px-4 py-3.5">
                              <div className="flex max-w-[340px] flex-wrap items-center gap-1">
                                {!row.worstChecks.length ? <span className="text-sm text-slate-400">{EM_DASH}</span> : null}
                                {visibleWorstChecks.map((check) => (
                                  <HealthCheckScoreBadge
                                    key={`${row.id}:${check.healthCheckKey}`}
                                    api={api}
                                    healthCheckKey={check.healthCheckKey}
                                    score={check.score}
                                  />
                                ))}
                                {row.worstChecks.length > 3 && !showAllWorstChecks ? (
                                  <button
                                    type="button"
                                    className="rounded-md px-1.5 py-0.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/[0.07]"
                                    onClick={() => {
                                      setExpandedWorstCheckRows((previous) => {
                                        const next = new Set(previous);
                                        next.add(row.id);
                                        return next;
                                      });
                                    }}
                                  >
                                    +{row.worstChecks.length - 3} more
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          ) : null}
                          {selectedTaxonomyColumns.map((facet) => (
                            <td key={`${row.id}:${facet}`} className="px-4 py-3.5 text-sm text-slate-500">
                              {row.taxonomyValuesByFacet[facet]?.length ? (
                                <div className="flex max-w-[320px] flex-wrap gap-1">
                                  {row.taxonomyValuesByFacet[facet]!.map((termKey) => (
                                    <TaxonomyTermNameBadge
                                      key={`${row.id}:${facet}:${termKey}`}
                                      api={api}
                                      termKey={termKey}
                                      termFacet={facet}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400">{EM_DASH}</span>
                              )}
                            </td>
                          ))}
                          <td className="px-4 py-3.5 whitespace-nowrap text-sm text-slate-500">{row.age}</td>
                          <td className="px-4 py-3.5 text-right">
                            {row.url ? (
                              <a
                                href={row.url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 no-underline ring-1 ring-inset ring-slate-200 transition-all duration-150 hover:bg-white hover:text-primary hover:no-underline hover:ring-primary/40 hover:shadow-sm"
                              >
                                View
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[0.625rem]" />
                              </a>
                            ) : (
                              <span className="text-xs text-slate-400">{EM_DASH}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>

          {pagedRows.length || isReloading ? (
            <BrandPresencePagination
              page={safePage}
              numberOfPages={numberOfPages}
              itemsPerPage={pageSize}
              totalNumberOfItems={totalNumberOfItems}
              itemLabel={totalNumberOfItems === 1 ? 'page' : 'pages'}
              onPageChange={setPage}
              pageSizeOptions={BRAND_PRESENCE_LIST_PAGE_SIZES}
              onPageSizeChange={setPageSize}
            />
          ) : null}
        </section>
      </main>
    </div>
  );
}

/** Rows settle in one after another, top to bottom. Literal classes so Tailwind emits them. */
const ROW_ENTRANCE_DELAYS = [
  '[--bp-delay:0ms]',
  '[--bp-delay:25ms]',
  '[--bp-delay:50ms]',
  '[--bp-delay:75ms]',
  '[--bp-delay:100ms]',
  '[--bp-delay:125ms]',
  '[--bp-delay:150ms]',
  '[--bp-delay:175ms]',
  '[--bp-delay:200ms]',
  '[--bp-delay:225ms]',
];
